import { type ISODate, addDays, parseISODate, startOfWeek, weekday } from './dates';
import { WEEKDAYS_LONG, fmtShort } from './format';
import type { Frequency, Routine } from './types';

type RoutineRule = Pick<Routine, 'frequency' | 'startsOn' | 'active'>;

/** Feriados que não contam como dia útil (calendário ANBIMA, vindo do banco). */
export type HolidayCheck = (s: ISODate) => boolean;
const noHolidays: HolidayCheck = () => false;

/** A rotina cai nesta data? Espelha app_private.occurs_on no banco. */
export function occursOn(r: RoutineRule, s: ISODate, isHoliday: HolidayCheck = noHolidays): boolean {
  if (!r.active) return false;
  const f = r.frequency, start = r.startsOn;
  if (s < start) return false;
  const n = Math.max(1, Number(f.interval) || 1), d = parseISODate(s), w = d.getDay(), d0 = parseISODate(start);
  switch (f.type) {
    case 'daily':
      if (f.businessDaysOnly && (w === 0 || w === 6 || isHoliday(s))) return false;
      return Math.round((d.getTime() - d0.getTime()) / 864e5) % n === 0;
    case 'weekly':
      if (!f.weekdays.includes(w)) return false;
      return Math.round((parseISODate(startOfWeek(s)).getTime() - parseISODate(startOfWeek(start)).getTime()) / (7 * 864e5)) % n === 0;
    case 'monthly': {
      const months = (d.getFullYear() - d0.getFullYear()) * 12 + d.getMonth() - d0.getMonth();
      if (months % n !== 0) return false;
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      return f.dayOfMonth === 'last' ? d.getDate() === last : d.getDate() === Math.min(Number(f.dayOfMonth) || 1, last);
    }
    case 'yearly':
      if ((d.getFullYear() - d0.getFullYear()) % n !== 0) return false;
      return d.getMonth() === d0.getMonth() && d.getDate() === d0.getDate();
  }
}

export function describeFrequency(f: Frequency, startsOn: ISODate): string {
  const n = Math.max(1, Number(f.interval) || 1);
  switch (f.type) {
    case 'daily':
      return n === 1 ? (f.businessDaysOnly ? 'Todo dia útil' : 'Todo dia') : `A cada ${n} dias${f.businessDaysOnly ? ', só dias úteis' : ''}`;
    case 'weekly': {
      const ds = [...f.weekdays].sort().map(d => WEEKDAYS_LONG[d]);
      const l = ds.length > 1 ? ds.slice(0, -1).join(', ') + ' e ' + ds[ds.length - 1] : ds[0] || 'nenhum dia';
      return n === 1 ? `Toda semana: ${l}` : `A cada ${n} semanas: ${l}`;
    }
    case 'monthly': {
      const day = f.dayOfMonth === 'last' ? 'no último dia' : `dia ${f.dayOfMonth || 1}`;
      return n === 1 ? `Todo mês, ${day}` : n === 3 ? `Todo trimestre, ${day}` : `A cada ${n} meses, ${day}`;
    }
    case 'yearly':
      return `${n === 1 ? 'Todo ano' : `A cada ${n} anos`}, em ${fmtShort(startsOn)}`;
  }
}

export function nextOccurrences(r: RoutineRule, today: ISODate, k = 4, isHoliday: HolidayCheck = noHolidays): ISODate[] {
  const res: ISODate[] = [];
  let s = r.startsOn > today ? r.startsOn : today;
  for (let i = 0; i < 800 && res.length < k; i++) {
    if (occursOn({ ...r, active: true }, s, isHoliday)) res.push(s);
    s = addDays(s, 1);
  }
  return res;
}

/**
 * Datas para as quais a rotina deve gerar tarefas agora.
 * Gera até `hoje + antecedência`, retomando de onde parou e nunca olhando
 * mais de 14 dias para trás. Devolve também o novo marcador `generatedThrough`.
 */
export function datesToGenerate(r: Pick<Routine, 'frequency' | 'startsOn' | 'active' | 'leadDays' | 'generatedThrough'>, today: ISODate, isHoliday: HolidayCheck = noHolidays) {
  const until = addDays(today, Math.max(0, Number(r.leadDays) || 0));
  if (!r.active) return { dates: [] as ISODate[], through: r.generatedThrough };
  let s = r.generatedThrough ? addDays(r.generatedThrough, 1) : r.startsOn;
  const floor = addDays(today, -14);
  if (s < floor) s = floor;
  if (s < r.startsOn) s = r.startsOn;
  const dates: ISODate[] = [];
  for (let d = s; d <= until; d = addDays(d, 1)) if (occursOn(r, d, isHoliday)) dates.push(d);
  return { dates, through: until };
}

export const isWeekend = (s: ISODate) => [0, 6].includes(weekday(s));
